import { logError } from '@/services/exceptions'
import ErrorCodes from '@/services/exceptions/ErrorCodes'
import { migrateAddedSafes } from '@/services/ls-migration/addedSafes'
import { migrateAddressBook } from '@/services/ls-migration/addressBook'
import { isChecksummedAddress } from '@/utils/addresses'
import type { AddressBook, AddressBookState } from '@/store/addressBookSlice'
import type { AddedSafesState } from '@/store/addedSafesSlice'
import type { CustomTokensState } from '@/store/customTokensSlice'
import type { AddedTxsState } from '@/store/addedTxsSlice'
import type { SafeAppsState } from '@/store/safeAppsSlice'
import type { SettingsState } from '@/store/settingsSlice'
import type { ChainInfo } from '@/store/customChainsSlice'
import getChainsConfig from '@/config/supportedChains'

import { useMemo } from 'react'

export const enum SAFE_EXPORT_VERSION {
  V1 = '1.0',
  V2 = '2.0',
  V2_5 = '2.5', // Note: 2.5 is chosen specifically to be different from versions from the safe team
  // https://github.com/safe-global/safe-wallet-monorepo/blob/85aaeafca7a646fcc658a12d67c2e57808f226b7/apps/web/src/components/settings/DataManagement/useGlobalImportFileParser.ts#L52
}

export enum ImportErrors {
  INVALID_VERSION = 'The file is not an Eternal Safe export.',
  INVALID_JSON_FORMAT = 'The JSON format is invalid.',
  NO_IMPORT_DATA_FOUND = 'This file contains no importable data.',
}

const WEB_URL_PROTOCOLS = new Set(['http:', 'https:'])
const RPC_URL_PROTOCOLS = new Set(['http:', 'https:', 'ws:', 'wss:'])
const CHAIN_ID_REGEX = /^[1-9]\d*$/
const SHORT_NAME_REGEX = /^[a-zA-Z0-9-]+$/
const BUILT_IN_CHAINS = getChainsConfig()
const BUILT_IN_CHAIN_IDS = new Set(BUILT_IN_CHAINS.map((chain) => chain.chainId))
const BUILT_IN_SHORT_NAMES = new Set(BUILT_IN_CHAINS.map((chain) => chain.shortName.toLowerCase()))

const isUrlWithAllowedProtocol = (url: string, allowedProtocols: Set<string>): boolean => {
  try {
    return allowedProtocols.has(new URL(url).protocol)
  } catch {
    return false
  }
}

const isEthAddress = (value?: string): boolean => {
  if (!value) return false
  return /^0x[a-fA-F0-9]{40}$/.test(value)
}

const isValidImportedCustomChain = (chain: unknown): chain is ChainInfo => {
  if (!chain || typeof chain !== 'object') {
    return false
  }

  const candidate = chain as Partial<ChainInfo>

  if (!candidate.chainId || typeof candidate.chainId !== 'string') {
    return false
  }
  if (!CHAIN_ID_REGEX.test(candidate.chainId)) {
    return false
  }

  if (!candidate.chainName || typeof candidate.chainName !== 'string') {
    return false
  }

  if (!candidate.shortName || typeof candidate.shortName !== 'string') {
    return false
  }
  if (!SHORT_NAME_REGEX.test(candidate.shortName)) {
    return false
  }

  const rpc = candidate.rpcUri?.value
  const publicRpc = candidate.publicRpcUri?.value
  const safeAppsRpc = candidate.safeAppsRpcUri?.value

  if (!rpc || !isUrlWithAllowedProtocol(rpc, RPC_URL_PROTOCOLS)) {
    return false
  }

  if (!publicRpc || !isUrlWithAllowedProtocol(publicRpc, RPC_URL_PROTOCOLS)) {
    return false
  }

  if (!safeAppsRpc || !isUrlWithAllowedProtocol(safeAppsRpc, RPC_URL_PROTOCOLS)) {
    return false
  }

  const explorerAddressUrl = candidate.blockExplorerUriTemplate?.address
  const explorerTxUrl = candidate.blockExplorerUriTemplate?.txHash

  if (explorerAddressUrl && !isUrlWithAllowedProtocol(explorerAddressUrl, WEB_URL_PROTOCOLS)) {
    return false
  }

  if (explorerTxUrl && !isUrlWithAllowedProtocol(explorerTxUrl, WEB_URL_PROTOCOLS)) {
    return false
  }

  const hasAnyMultisendOverride = !!candidate.multisendAddress || !!candidate.multisendCallOnlyAddress
  if (hasAnyMultisendOverride) {
    if (!isEthAddress(candidate.multisendAddress) || !isEthAddress(candidate.multisendCallOnlyAddress)) {
      return false
    }
  }

  return true
}

const sanitizeImportedCustomChains = (chains: unknown): ChainInfo[] | undefined => {
  if (!Array.isArray(chains)) {
    return undefined
  }

  const byChainId = new Map<string, ChainInfo>()
  const shortNames = new Set<string>()

  for (const chain of chains) {
    if (!isValidImportedCustomChain(chain)) {
      continue
    }

    const normalizedShortName = chain.shortName.toLowerCase()

    if (BUILT_IN_CHAIN_IDS.has(chain.chainId) || BUILT_IN_SHORT_NAMES.has(normalizedShortName)) {
      continue
    }
    if (byChainId.has(chain.chainId) || shortNames.has(normalizedShortName)) {
      continue
    }

    shortNames.add(normalizedShortName)
    byChainId.set(chain.chainId, {
      ...chain,
      custom: true,
    })
  }

  return byChainId.size > 0 ? Array.from(byChainId.values()) : undefined
}

const countEntries = (data: { [chainId: string]: { [address: string]: unknown } }) =>
  Object.values(data).reduce<number>((count, entry) => count + Object.keys(entry).length, 0)

export const _filterValidAbEntries = (ab?: AddressBookState): AddressBookState | undefined => {
  if (!ab) {
    return undefined
  }

  return Object.entries(ab).reduce<AddressBookState>((acc, [chainId, chainAb]) => {
    const sanitizedChainAb = Object.entries(chainAb).reduce<AddressBook>((acc, [address, name]) => {
      // Legacy imported address books could have undefined name or address entries
      if (name?.trim() && address && isChecksummedAddress(address)) {
        acc[address] = name
      }
      return acc
    }, {})

    if (Object.keys(sanitizedChainAb).length > 0) {
      acc[chainId] = sanitizedChainAb
    }

    return acc
  }, {})
}

/**
 * The global import currently imports:
 * 1.0:
 *  - address book
 *  - added Safes
 *
 * 2.0:
 *  - address book
 *  - added Safes
 *  - safeApps
 *  - settings
 *  - added txs
 *  - customTokens
 *
 * 2.5: eternalsafe exclusive!
 *  - address book
 *  - added Safes
 *  - safeApps
 *  - settings
 *  - added txs
 *  - customTokens
 *  - customChains
 *
 * @param jsonData
 * @returns data to import and some insights about it
 */

type Data = {
  addedSafes?: AddedSafesState
  addressBook?: AddressBookState
  customTokens?: CustomTokensState
  customChains?: ChainInfo[]
  addedTxs?: AddedTxsState
  settings?: SettingsState
  safeApps?: SafeAppsState
  error?: ImportErrors
  addressBookEntriesCount: number
  addedSafesCount: number
}

export const useGlobalImportJsonParser = (jsonData: string | undefined): Data => {
  return useMemo(() => {
    const data: Data = {
      addressBookEntriesCount: 0,
      addedSafesCount: 0,
      addressBook: undefined,
      addedSafes: undefined,
      customTokens: undefined,
      customChains: undefined,
      addedTxs: undefined,
      settings: undefined,
      safeApps: undefined,
    }

    if (!jsonData) {
      return data
    }

    let parsedFile

    try {
      parsedFile = JSON.parse(jsonData)
    } catch (err) {
      logError(ErrorCodes._704, err)

      data.error = ImportErrors.INVALID_JSON_FORMAT
      return data
    }

    if (!parsedFile.data || Object.keys(parsedFile.data).length === 0) {
      data.error = ImportErrors.NO_IMPORT_DATA_FOUND
      return data
    }

    switch (parsedFile.version) {
      case SAFE_EXPORT_VERSION.V1: {
        data.addressBook = migrateAddressBook(parsedFile.data) ?? undefined
        data.addedSafes = migrateAddedSafes(parsedFile.data) ?? undefined

        break
      }

      case SAFE_EXPORT_VERSION.V2: {
        data.addressBook = _filterValidAbEntries(parsedFile.data.addressBook)
        data.addedSafes = parsedFile.data.addedSafes
        data.customTokens = parsedFile.data.customTokens
        data.addedTxs = parsedFile.data.addedTxs
        data.settings = parsedFile.data.settings
        data.safeApps = parsedFile.data.safeApps

        break
      }

      case SAFE_EXPORT_VERSION.V2_5: {
        data.addressBook = _filterValidAbEntries(parsedFile.data.addressBook)
        data.addedSafes = parsedFile.data.addedSafes
        data.customTokens = parsedFile.data.customTokens
        data.addedTxs = parsedFile.data.addedTxs
        data.settings = parsedFile.data.settings
        data.safeApps = parsedFile.data.safeApps
        data.customChains = sanitizeImportedCustomChains(parsedFile.data.customChains)

        break
      }

      default: {
        data.error = ImportErrors.INVALID_VERSION
      }
    }

    data.addressBookEntriesCount = data.addressBook ? countEntries(data.addressBook) : 0
    data.addedSafesCount = data.addedSafes ? countEntries(data.addedSafes) : 0

    return data
  }, [jsonData])
}
